import { defaultApi } from "@/api";
import { defineStore } from "pinia";
type SelectOption = { label: string; value: string };

const toTitleCase = (str: string): string => {
  if (!str || typeof str !== "string") return str;
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export interface FetchSelectOptionsConfig {
  key?: string;
  labelField?: string;
  valueField?: string;
  [key: string]: any;
}

// A keyed array can hold plain strings (legacy lookups) or objects that need
// labelField/valueField extraction (e.g. bookings' `{ results: [{code,name}] }`).
const mapKeyedItems = (items: any[], labelField: string, valueField: string): SelectOption[] =>
  items.map((item) =>
    typeof item === "string"
      ? { label: item, value: item }
      : { ...item, label: item[labelField] ?? item, value: item[valueField] ?? item }
  );

export const useLookupStore = defineStore("lookup", {
  state: () => ({
    lookupStore: {} as Record<string, SelectOption[]>,
    lookupTotal: {} as Record<string, number>, // server-reported total per filter key
    cache: new Map<string, SelectOption[]>(),
    inFlightRequests: new Map<string, Promise<SelectOption[]>>(),
    prefetchedLookups: {} as Record<string, Record<string, SelectOption[]>>
  }),

  actions: {
    async loadLookup(
      name: string,
      url: string,
      config: FetchSelectOptionsConfig = {},
      forceRefresh = false,
      isDynamic = false
    ): Promise<SelectOption[]> {
      if (!isDynamic && !forceRefresh && this.cache.has(url)) {
        const cachedOptions = this.cache.get(url)!;
        this.lookupStore[name] = cachedOptions;
        return cachedOptions;
      }

      if (!isDynamic && !forceRefresh && this.inFlightRequests.has(url)) {
        const ongoingRequest = this.inFlightRequests.get(url)!;
        const result = await ongoingRequest;
        this.lookupStore[name] = result;
        return result;
      }

      const fetchPromise = this.fetchSelectOptions(url, config);

      if (!isDynamic) {
        this.inFlightRequests.set(url, fetchPromise);
      }

      try {
        const options = await fetchPromise;

        if (!isDynamic) {
          this.cache.set(url, options);
        }

        this.lookupStore[name] = options;
        return options;
      } finally {
        if (!isDynamic) {
          this.inFlightRequests.delete(url);
        }
      }
    },

    async fetchSelectOptions(url: string, config: FetchSelectOptionsConfig = {}): Promise<SelectOption[]> {
      const { key, labelField = "name", valueField = "code" } = config;

      try {
        const res = await defaultApi.apiUnversioned.lookup.getAll(url, { params: { ...config.params } });
        const data = res.data.data ?? res.data;

        if (key && Array.isArray(data[key])) {
          return mapKeyedItems(data[key], labelField, valueField);
        }

        if (Array.isArray(data)) {
          return mapKeyedItems(data, labelField, valueField);
        }

        console.warn("Unknown lookup response format:", data);
        return [];
      } catch (error) {
        console.error("Lookup fetch failed:", error);
        return [];
      }
    },

    appendToLookup(name: string, newOptions: SelectOption[]) {
      const existing = this.lookupStore[name] || [];
      this.lookupStore[name] = [...existing, ...newOptions];
    },

    async fetchPagedOptions(name: string, url: string, config: FetchSelectOptionsConfig = {}): Promise<SelectOption[]> {
      const { key, labelField = "name", valueField = "code" } = config;

      try {
        const res = await defaultApi.apiUnversioned.lookup.getAll(url, { params: { ...config.params } });
        const data = res.data.data ?? res.data;
        const pagination = res.data?.pagination;

        if (pagination != null) {
          const total = pagination.total ?? pagination.totalCount ?? null;
          if (total !== null) this.lookupTotal[name] = total;
        }

        if (key && Array.isArray(data[key])) {
          return mapKeyedItems(data[key], labelField, valueField);
        }

        if (Array.isArray(data)) {
          return mapKeyedItems(data, labelField, valueField);
        }

        return [];
      } catch (error) {
        console.error("Lookup fetchPagedOptions failed:", error);
        return [];
      }
    },

    async prefetchLookup(url: string, zone: string, valueKey?: string) {
      try {
        const res = await defaultApi.apiUnversioned.lookup.getAll(url);

        // Handle different response formats
        const responseData = res.data;

        if (Array.isArray(responseData)) {
          // If response is directly an array of objects with name/code
          const options = responseData.map((item: any) => {
            if (typeof item === "string") {
              return { label: item, value: item };
            }
            const baseLabel = item.name ?? item.label ?? item;
            const displayLabel = item.location?.countryName
              ? `${toTitleCase(baseLabel)}, ${toTitleCase(item.location.countryName)}`
              : toTitleCase(baseLabel);
            return {
              label: displayLabel,
              value: valueKey ? (item[valueKey] ?? item.code ?? item.value ?? item) : (item.code ?? item.value ?? item)
            };
          });

          // Sort all options alphabetically by label
          options.sort((a: any, b: any) => a.label.localeCompare(b.label));

          // Store as a single 'items' key for direct array responses
          this.prefetchedLookups[zone] = { items: options };
        } else if (responseData && typeof responseData === "object") {
          // If response is an object with key-value pairs
          this.prefetchedLookups[zone] = Object.fromEntries(
            Object.entries(responseData).map(([key, arr]) => {
              if (!Array.isArray(arr)) {
                return [key, []];
              }

              const mappedArr = arr.map((item: any) => {
                // Handle string arrays (old logic)
                if (typeof item === "string") {
                  return { label: item, value: item };
                }
                // Handle object arrays with name/code fields (new logic)
                const baseLabel = item.name ?? item.label ?? item;
                const displayLabel = item.location?.countryName
                  ? `${toTitleCase(baseLabel)}, ${toTitleCase(item.location.countryName)}`
                  : toTitleCase(baseLabel);
                return {
                  label: displayLabel,
                  value: valueKey
                    ? (item[valueKey] ?? item.code ?? item.value ?? item)
                    : (item.code ?? item.value ?? item)
                };
              });

              return [key, mappedArr.sort((a: any, b: any) => a.label.localeCompare(b.label))];
            })
          );
        }
      } catch (error) {
        console.error("Prefetch lookup failed:", error);
      }
    }
  }
});
